import {
  ApiContent,
  ApiResponse,
  ContentResponse,
  GetContentsParams,
  GetContentsParams2,
  PaginatedContentResponse,
  UpdateContentRequest,
} from '@/types';
import apiSlice from './apiSlice';

export interface CreateContentRequest {
  title: string;
  description: string;
  categoryId?: number;
  audioUploadToken?: string;
  videoDuration?: number;
  duration?: number;
  audioKey?: string;
  audioUrl?: string;
  videoUrl?: string;
  embedUrl?: string;
  imageUrl?: string;
  benefits?: string;
  isLocked?: boolean;
  mantra?: string;
  mantraInEnglish?: string;
  translation?: string;
  sanskritMantra?: string;
  englishTranslation?: string;
  englishPronunciation?: string;
  productLink?: string;
  personalizedProductLink?: string;
  iconUrl?: string;
}

export const contentApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createContent: builder.mutation<void, CreateContentRequest>({
      query: (data) => ({
        url: '/contents',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Contents', id: 'LIST' }],
    }),
    // getContents: builder.query<PaginatedContentResponse, GetContentsParams>({
    //   query: ({ isLocked, skip, take }) => ({
    //     url: '/contents',
    //     params: { isLocked, skip, take },
    //   }),
    //   providesTags: (result) =>
    //     result
    //       ? [
    //           ...result.data.map(({ id }) => ({
    //             type: 'Contents' as const,
    //             id,
    //           })),
    //           { type: 'Contents', id: 'LIST' },
    //         ]
    //       : [{ type: 'Contents', id: 'LIST' }],
    // }),

    getContents: builder.query<PaginatedContentResponse, GetContentsParams>({
      query: ({ isLocked, skip, take, search, fromDate, toDate, categoryId }) => ({
        url: '/contents',
        params: { isLocked, skip, take, search, fromDate, toDate, categoryId },
      }),

      providesTags: (result) => [{ type: 'Contents', id: 'LIST' }],
    }),
    getContentsByCategoryId: builder.query<PaginatedContentResponse, GetContentsParams2>({
      query: ({ skip, take, search, fromDate, toDate, categoryId }) => ({
        url: '/contents/category',
        params: { skip, take, search, fromDate, toDate, categoryId },
      }),
      providesTags: [{ type: 'Contents', id: 'LIST' }],
    }),

    getContentById: builder.query<ContentResponse, number>({
      query: (id) => ({
        url: `/contents/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: ContentResponse | ApiResponse<ContentResponse>) =>
        'data' in response ? response.data : response,
      providesTags: (result, error, id) => [{ type: 'Contents', id }],
    }),

    deleteContent: builder.mutation<void, number>({
      query: (id) => ({
        url: `/contents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Contents', id },
        { type: 'Contents', id: 'LIST' },
      ],
    }),

    streamContent: builder.query<{ audioUrl: string }, number>({
      query: (id) => ({
        url: `/contents/${id}/stream`,
        method: 'GET',
      }),
    }),

    updateContent: builder.mutation<ApiContent, UpdateContentRequest>({
      query: ({ id, ...body }) => {
        const hasFilePayload = Object.values(body).some((value) => value instanceof File);

        if (!hasFilePayload) {
          return {
            url: `/contents/${id}`,
            method: 'PATCH',
            body,
          };
        }

        const formData = new FormData();

        Object.entries(body).forEach(([key, value]) => {
          if (value === undefined || value === null) return;

          if (value instanceof File) {
            formData.append(key, value);
            return;
          }

          formData.append(key, String(value));
        });

        return {
          url: `/contents/${id}`,
          method: 'PATCH',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Contents', id },
        { type: 'Contents', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useCreateContentMutation,
  useGetContentsQuery,
  useGetContentByIdQuery,
  useGetContentsByCategoryIdQuery,
  useDeleteContentMutation,
  useUpdateContentMutation,
  useStreamContentQuery,
} = contentApi;
